import React, { useState, useCallback } from 'react'
import { Modal } from '../ui/Modal'
import { Button } from '../ui/Button'
import { X, Info, Upload } from 'lucide-react'

interface VendorResponse {
  vendorName: string
  files: File[]
}

interface UploadVendorResponsesModalProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (responses: VendorResponse[]) => void
}

export const UploadVendorResponsesModal: React.FC<UploadVendorResponsesModalProps> = ({
  isOpen,
  onClose,
  onSubmit
}) => {
  const [vendors, setVendors] = useState<VendorResponse[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isAddingMode, setIsAddingMode] = useState(false)

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    if (!e.currentTarget.contains(e.relatedTarget as Node)) {
      setIsDragOver(false)
    }
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)

    const items = Array.from(e.dataTransfer.items)
    processDroppedItems(items)
  }

  const processDroppedItems = async (items: DataTransferItem[]) => {
    const newVendors: VendorResponse[] = []
    const promises: Promise<void>[] = []

    for (const item of items) {
      if (item.kind === 'file') {
        const entry = item.webkitGetAsEntry()
        if (entry) {
          if (entry.isDirectory) {
            // Process folder - create a promise for each folder
            const promise = readDirectory(entry as FileSystemDirectoryEntry).then(files => {
              if (files.length > 0) {
                newVendors.push({
                  vendorName: entry.name,
                  files: files
                })
              }
            })
            promises.push(promise)
          } else if (entry.isFile) {
            // Skip individual files - only folders are supported
            console.warn('Individual files are not supported. Please organize files into vendor folders.')
          }
        }
      }
    }

    // Wait for all folder processing to complete
    await Promise.all(promises)
    
    // Show warning if no valid vendor folders were found
    if (newVendors.length === 0) {
      console.warn('No valid vendor folders found. Please make sure to drag folders (not individual files) containing vendor proposal documents.')
      return
    }
    
    // Merge with existing vendors (replace duplicates)
    const updatedVendors = [...vendors]
    let addedCount = 0
    let duplicateCount = 0
    
    newVendors.forEach(newVendor => {
      const existingIndex = updatedVendors.findIndex(v => v.vendorName.toLowerCase() === newVendor.vendorName.toLowerCase())
      
      if (existingIndex >= 0) {
        // Duplicate found - replace the existing vendor
        updatedVendors[existingIndex] = newVendor
        duplicateCount++
      } else {
        // New vendor - add to list
        updatedVendors.push(newVendor)
        addedCount++
      }
    })
    
    setVendors(updatedVendors)
    setIsAddingMode(true)
    
    // Provide detailed user feedback
    console.log('🎯 VENDOR UPLOAD SUMMARY:')
    newVendors.forEach(vendor => {
      console.log(`📂 ${vendor.vendorName}: ${vendor.files.length} files`)
      vendor.files.forEach(file => {
        console.log(`  📄 ${file.name} (${(file.size / 1024).toFixed(1)} KB)`)
      })
    })

    if (duplicateCount > 0) {
      console.log(`✅ Added ${addedCount} new vendor(s) and replaced ${duplicateCount} existing vendor(s)`)
    } else {
      console.log(`✅ Added ${addedCount} vendor(s) to selection`)
    }

    const totalFiles = updatedVendors.reduce((sum, vendor) => sum + vendor.files.length, 0)
    console.log(`📊 Total: ${updatedVendors.length} vendors, ${totalFiles} files`)
  }

  const readDirectory = async (directoryEntry: FileSystemDirectoryEntry): Promise<File[]> => {
    const files: File[] = []
    const dirReader = directoryEntry.createReader()

    // Read all entries (may require multiple calls due to browser batching)
    let allEntries: FileSystemEntry[] = []
    let batch: FileSystemEntry[]

    console.log(`📁 Reading directory: ${directoryEntry.name}`)

    do {
      batch = await new Promise<FileSystemEntry[]>((resolve) => {
        dirReader.readEntries((entries) => {
          console.log(`📄 Read batch of ${entries.length} entries`)
          resolve(entries)
        })
      })
      allEntries = allEntries.concat(batch)
    } while (batch.length > 0)

    console.log(`📄 Total entries found: ${allEntries.length}`)

    // Process all file entries
    for (const entry of allEntries) {
      if (entry.isFile) {
        try {
          const file = await new Promise<File>((resolve, reject) => {
            (entry as FileSystemFileEntry).file((f) => {
              console.log(`📄 Processing file: ${f.name} (${f.type})`)
              resolve(f)
            }, reject)
          })

          // Accept all common document and image types based on file extension
          const allowedExtensions = [
            '.pdf', '.doc', '.docx', '.txt', '.rtf',  // Documents
            '.xls', '.xlsx', '.xlsm', '.csv',          // Spreadsheets
            '.png', '.jpg', '.jpeg', '.gif', '.bmp', '.tif', '.tiff',   // Images
            '.zip', '.rar'                             // Archives
          ]

          const fileExt = file.name.toLowerCase().substring(file.name.lastIndexOf('.'))

          if (allowedExtensions.includes(fileExt)) {
            files.push(file)
            console.log(`✅ Added file: ${file.name} (${fileExt})`)
          } else {
            console.log(`❌ Skipped file: ${file.name} (${fileExt}) - not in allowed types`)
          }
        } catch (error) {
          console.error(`❌ Error reading file ${entry.name}:`, error)
        }
      }
    }

    console.log(`📁 Directory ${directoryEntry.name} processed: ${files.length} valid files`)
    return files
  }


  const handleSubmit = async () => {
    if (vendors.length > 0) {
      setIsLoading(true)
      try {
        await onSubmit(vendors)
        setVendors([])
        onClose()
      } finally {
        setIsLoading(false)
      }
    }
  }

  const clearAllVendors = () => {
    setVendors([])
    setIsAddingMode(false)
  }


  const getFolderCountText = () => {
    if (vendors.length === 0) return ''
    return vendors.length === 1 ? '1 folder selected' : `${vendors.length} folders selected`
  }

  const handleModalClose = () => {
    setVendors([])
    setIsAddingMode(false)
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleModalClose} title="Upload vendor proposals" size="xl">
      <div>
          {/* Info Message */}
          <div className="flex items-start gap-2 mb-4">
            <Info className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-slate-500">
              Drag and drop vendor folders here. Each folder should be named after the vendor and contain their proposal files.
              Only folders are accepted - individual files will be ignored.
            </p>
          </div>

          {/* Folder Count and Clear All */}
          {vendors.length > 0 && (
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-slate-700">
                {getFolderCountText()}
              </span>
              <button
                onClick={clearAllVendors}
                className="text-sm text-rose-600 hover:bg-rose-50 rounded-md px-2 py-1 font-medium"
                disabled={isLoading}
              >
                Clear All
              </button>
            </div>
          )}

          {/* Drop Zone — DS compact variant, per sop-studio */}
          <div
            role="button"
            tabIndex={0}
            className={`group flex flex-col items-center justify-center gap-3 text-center cursor-pointer rounded-xl border-2 border-dashed px-6 py-12 transition-[background-color,border-color,box-shadow] duration-200 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-700 focus-visible:ring-offset-2 ${
              isDragOver
                ? 'border-accent-500 bg-accent-100 shadow-md'
                : 'border-accent-300 bg-accent-50/60 hover:border-accent-400 hover:bg-accent-50 hover:shadow-sm'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div
              className={`flex h-12 w-12 items-center justify-center rounded-full ring-4 ring-accent-50 transition-transform duration-200 ease-out group-hover:-translate-y-0.5 ${
                isDragOver ? 'bg-accent-500 text-white' : 'bg-accent-100 text-accent-600'
              }`}
            >
              <Upload className="w-5 h-5" />
            </div>
            <div className="text-sm font-medium text-slate-700">
              Drag &amp; drop vendor folders here
            </div>
            <span className="text-[11px] text-slate-500">Folders only — one per vendor</span>
          </div>

        {/* Vendor List */}
        {vendors.length > 0 && (
          <div className="mt-4">
            <h3 className="text-xs font-semibold mb-2">
              Detected Vendors ({vendors.length})
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {vendors.map((vendor, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-md">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{vendor.vendorName}</p>
                    <p className="text-sm text-slate-600">{vendor.files.length} file(s)</p>
                  </div>
                  <button
                    onClick={() => setVendors(vendors.filter((_, i) => i !== index))}
                    className="text-slate-400 hover:text-red-500"
                    disabled={isLoading}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-2 mt-5 pt-3 border-t border-slate-100">
          <Button
            onClick={handleSubmit}
            disabled={vendors.length === 0 || isLoading}
          >
            {isLoading ? 'Importing...' : 'Import'}
          </Button>
        </div>
      </div>
    </Modal>
  )
}